/**
 * SAID-DIIS — Record Clerk Portal shell.
 *
 * Rendered from App.js only when `user.role === 'clerk'`. Provides its own
 * top bar, side navigation and page routing (internal state, no react-router
 * to avoid touching the rest of the app). Consumes the same axios auth
 * header wired up by /api/client.js.
 *
 * Every data fetch here hits an existing, unmodified backend endpoint.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutGrid, ClipboardCheck, PackageCheck, Recycle, Archive,
  LogOut, KeyRound, UserCog, ChevronDown,
} from 'lucide-react';
import Avatar from '../components/Avatar';
import './styles.css';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { Toast } from './ui';
import HomePage from './pages/Home';
import IssuePage from './pages/Issue';
import VerifyPage from './pages/Verify';
import AddUsedPage from './pages/AddUsed';
import OlderQueuePage from './pages/OlderQueue';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png';

const NAV_ITEMS = [
  { id: 'home',    label: 'Desk',              icon: LayoutGrid },
  { id: 'issue',   label: 'Issue Components',  icon: ClipboardCheck },
  { id: 'verify',  label: 'Verify Returns',    icon: PackageCheck },
  { id: 'used',    label: 'Add Used Component', icon: Recycle },
  { id: 'older',   label: 'Older Stock Queue', icon: Archive },
];

export default function ClerkPortal({ user, onChangePassword, onEditProfile, onLogout }) {
  const [page, setPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, variant = 'ok') => {
    setToast({ message, variant });
    window.setTimeout(() => setToast(null), 4200);
  }, []);

  // ── Global data slices (shared across pages so counts stay consistent) ──
  const [pendingIssuance, setPendingIssuance] = useState([]);
  const [pendingReturns, setPendingReturns]   = useState([]);
  const [olderPending, setOlderPending]       = useState([]);
  const [components, setComponents]           = useState([]);
  const [brands, setBrands]                   = useState([]);
  const [loading, setLoading]                 = useState(true);

  const loadIssuance = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.returns.clerkPendingIssuance);
      setPendingIssuance(Array.isArray(data?.orders) ? data.orders : []);
    } catch (e) { console.error('pending-issuance', e); }
  }, []);
  const loadReturns = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.returns.clerkPendingList);
      setPendingReturns(Array.isArray(data) ? data : []);
    } catch (e) { console.error('clerk/returns', e); }
  }, []);
  const loadOlder = useCallback(async () => {
    try {
      const { data } = await apiClient.get(
        `${endpoints.olderStockReturns.list}?status_filter=pending_clerk_verification`
      );
      setOlderPending(Array.isArray(data) ? data : []);
    } catch (e) { console.error('older-stock-returns', e); }
  }, []);
  const loadCatalogue = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        apiClient.get(endpoints.brands.list),
        apiClient.get(endpoints.components.list),
      ]);
      setBrands(Array.isArray(b.data) ? b.data : []);
      setComponents(Array.isArray(c.data) ? c.data : []);
    } catch (e) { console.error('catalogue', e); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadIssuance(), loadReturns(), loadOlder(), loadCatalogue()]);
    setLoading(false);
  }, [loadIssuance, loadReturns, loadOlder, loadCatalogue]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Silent 30s auto-refresh of the operational queues (mirrors legacy behaviour).
  useEffect(() => {
    const t = window.setInterval(() => {
      loadIssuance(); loadReturns(); loadOlder();
    }, 30000);
    return () => window.clearInterval(t);
  }, [loadIssuance, loadReturns, loadOlder]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // ── Derived counts for indicators / nav badges ──
  const counts = useMemo(() => {
    // OVERDUE / EXCEPTIONS = approved orders sitting in issuance queue for >3 days.
    // This is a soft operational signal, computed client-side only.
    const now = Date.now();
    const overdueIssue = pendingIssuance.filter((o) => {
      const t = o.approved_at ? new Date(o.approved_at).getTime() : 0;
      return t && (now - t) > 3 * 24 * 60 * 60 * 1000;
    }).length;
    const staleReturns = pendingReturns.filter((r) => {
      const t = r.created_at ? new Date(r.created_at).getTime() : 0;
      return t && (now - t) > 3 * 24 * 60 * 60 * 1000;
    }).length;
    return {
      issue: pendingIssuance.length,
      returns: pendingReturns.length,
      older: olderPending.length,
      exceptions: overdueIssue + staleReturns,
    };
  }, [pendingIssuance, pendingReturns, olderPending]);

  const initials = (user?.full_name || user?.username || '·')
    .split(' ').filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');

  const ctx = {
    user,
    loading,
    pendingIssuance,
    pendingReturns,
    olderPending,
    components,
    brands,
    counts,
    showToast,
    goto: setPage,
    refresh: {
      all: refreshAll,
      issuance: loadIssuance,
      returns: loadReturns,
      older: loadOlder,
      catalogue: loadCatalogue,
    },
  };

  return (
    <div className="clerk-portal" data-testid="clerk-portal">
      {/* ── TOP BAR ────────────────────────────────────────── */}
      <header className="cp-topbar">
        <div className="cp-topbar-brand">
          <img className="cp-topbar-logo" src={LOGO_URL} alt="SAID" />
          <div className="cp-topbar-wordmark">
            <strong>SAID <span style={{ fontFamily: 'var(--diis-font-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.24em', color: 'var(--diis-ink-subtle)', marginLeft: 4, verticalAlign: 2 }}>— DIIS</span></strong>
            <span>Clinical Component Desk</span>
          </div>
        </div>

        <div className="cp-topbar-user" ref={menuRef}>
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
            <span className="cp-topbar-user-name">{user?.full_name || user?.username}</span>
            <span className="cp-topbar-user-role">Record Clerk</span>
          </div>
          <button
            type="button"
            className="cp-topbar-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="cp-user-menu-btn"
            aria-label="Account menu"
          >
            <span className="cp-avatar" aria-hidden="true" style={{ padding: 0, overflow: 'hidden' }}>
              <Avatar user={user} size={28} testId="cp-header-avatar" />
            </span>
            <ChevronDown size={14} />
          </button>

          {menuOpen && (
            <div className="cp-user-menu" role="menu" data-testid="cp-user-menu">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onEditProfile && onEditProfile(); }}
                data-testid="cp-edit-profile-btn"
              ><UserCog size={15} /> Edit profile</button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onChangePassword && onChangePassword(); }}
                data-testid="cp-change-password-btn"
              ><KeyRound size={15} /> Change password</button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                data-testid="cp-logout-btn"
              ><LogOut size={15} /> Sign out</button>
            </div>
          )}
        </div>
      </header>

      {/* ── LAYOUT ─────────────────────────────────────────── */}
      <div className="cp-layout">
        <nav className="cp-sidenav" aria-label="Clerk navigation">
          <div className="cp-nav-group-label">Operations</div>
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
            const active = page === id;
            let count;
            if (id === 'issue')  count = counts.issue || undefined;
            if (id === 'verify') count = counts.returns || undefined;
            if (id === 'older')  count = counts.older || undefined;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPage(id)}
                className={`cp-nav-item${active ? ' is-active' : ''}`}
                data-testid={`cp-nav-${id}`}
              >
                <Icon size={17} strokeWidth={1.7} />
                <span>{label}</span>
                {count ? <span className="cp-nav-count">{count}</span> : null}
              </button>
            );
          })}
        </nav>

        <main className="cp-main">
          <div className="cp-page">
            {page === 'home'   && <HomePage   ctx={ctx} />}
            {page === 'issue'  && <IssuePage  ctx={ctx} />}
            {page === 'verify' && <VerifyPage ctx={ctx} />}
            {page === 'used'   && <AddUsedPage ctx={ctx} />}
            {page === 'older'  && <OlderQueuePage ctx={ctx} />}
          </div>
        </main>
      </div>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
